import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const BhutanPage = () => {
  const bhutanData = {
    name: 'Bhutan',
    region: 'South Asia',
    color: '#7090A0',
    metaTitle: 'Pharma & Nutraceutical Registration in Bhutan | BFDA | CTD / ACTD Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Bhutan. BFDA requirements, CTD/ACTD dossier format, food supplement rules, key documents and market opportunity in a small but import-dependent Himalayan market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Bhutan',
      subtitle: 'A small, lower-middle income market with high reliance on imported medicines, a unified food–drug authority (BFDA), and CTD/ACTD-based registration pathways.',
      cta: 'Start Your Bhutan Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~786K' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure (% of GDP)', value: '~3.85%' },
      { label: 'Govt Health Spend (% of Budget)', value: '~10%' },
      { label: 'Pharma Imports (2023)', value: '~USD 8.9M' },
      { label: 'Regulatory Authority', value: 'BFDA' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Small, Structured, Import-Dependent',
      stats: [
        'Pharmaceutical imports ~USD 8.88 million in 2023',
        'Ranks 184th globally by import value',
        'Most pharmaceuticals imported from India and Bangladesh',
        '~55% of health spending on curative services (FY 2018/19)',
        'System now under pressure from rising NCDs',
        'Health services predominantly public with private pharmacies in urban centers',
        'Thimphu and Phuentsholing as main urban pharmaceutical hubs',
        'Unified Bhutan Food and Drug Authority (BFDA) regulates medicines and food'
      ],
      opportunities: [
        'High government health spending (~3% of GDP) for small economy',
        'Almost all pharmaceuticals imported - limited local manufacturing',
        'Highly centralized regulation under BFDA',
        'Strong dependence on imports creates focused opportunity',
        'Clear focal point for both medicines and food supplements'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: From Infectious Focus to NCD Pressure',
      subtitle: 'Bhutan is transitioning from communicable disease focus to rising NCD burden, with ongoing nutritional challenges.',
      categories: [
        {
          name: 'Non-Communicable Diseases — Growing Pressure',
          items: [
            'Adult obesity: 10.3% of women, 6.0% of men',
            'Diabetes: ~13.2% of women, ~13.9% of men',
            'Limited progress towards diet-related NCD targets',
            'Long-term need for cardiometabolic care and prevention',
            'Pharma: Antidiabetics, antihypertensives, lipid-lowering, cardiovascular & renal therapies',
            'Nutra: Metabolic support, cardioprotective supplements, weight-management, antioxidants for urban/high-risk groups'
          ]
        },
        {
          name: 'Infectious Diseases & Nutrition',
          items: [
            'Health spending historically focused on communicable diseases',
            'Large share of curative expenditure still on infectious diseases',
            'Undernutrition and micronutrient deficiencies improving but remain relevant',
            'Nutrition-related SDG profile gaps persist',
            'Pharma: Anti-infectives, vaccines (tenders/cooperation), paediatric medicines, basic acute care + chronic therapies',
            'Nutra: Micronutrient supplements (iron, folate, multivitamins), general wellness, paediatric & maternal products'
          ]
        },
        {
          name: 'Best-Fit Product Categories',
          items: [
            'Pharma: High-quality generics (antidiabetics, antihypertensives, lipid-lowering, key anti-infectives, paediatric)',
            'Nutra: Metabolic health, micronutrient supplements, cardio & antioxidant blends, maternal & child nutrition'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Bhutan Food and Drug Authority (BFDA)',
      description: 'BFDA formed by merging Drug Regulatory Authority (DRA), Bhutan Narcotics Control Authority (BNCA) and BAFRA under Civil Service Reform Act 2022. Regulates food, medicines, narcotics and agricultural products. Bhutan Medicines Rules and Regulation (BMRR) 2012/2019 with Guideline for Registration of Medicinal Products (2020).',
      pharma: {
        timeline: 'Varies based on dossier completeness and Registration Committee evaluation',
        requirements: [
          'ICH CTD or ASEAN CTD (ACTD) format required for standard route',
          'Bhutanese importer acts as applicant (Form I under BMRR)',
          'Import authorization via BFDA Government-to-Citizen (G2C) portal',
          'Detailed product profiles and technical documents from manufacturer',
          'Quality data, stability, specifications, manufacturing process',
          'Supporting safety/efficacy information',
          'Registration Committee evaluates for quality, safety, efficacy',
          'Market authorization issued under BMRR with conditions and validity',
          'Post-approval variations require CTD/ACTD documentation'
        ],
        insight: 'BFDA guidelines specify ICH CTD or ACTD dossiers for standard route. Registration Committee approved by Bhutan Medicines Board evaluates all submissions. Unified authority simplifies oversight.'
      },
      nutra: {
        timeline: 'Depends on classification and Food Act compliance',
        requirements: [
          'BFDA acts as overarching authority (drawing on DRA rules and Food Act)',
          'Food supplements registered under BMRR 2012 using Form VI-FPR',
          'Must meet safety, quality and labelling standards under Food Act 2005',
          'Local importer/firm as applicant required',
          'Full composition (qualitative + quantitative) and COAs',
          'Manufacturer GMP/quality certification',
          'Labelling in English/local language (ingredients, storage, expiry, claims)',
          'Clear classification as food supplement vs medicine to avoid mis-registration'
        ],
        insight: 'Responsibilities shared between food and drug domains. BAFRA regulates food/feed additives, while BFDA regulates feed supplements and medicinal aspects. Clear classification critical.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with BFDA',
      steps: [
        'Local Applicant / Importer - Bhutanese importer acts as applicant',
        'Application Form I under BMRR submission',
        'Import authorization via BFDA G2C portal',
        'Dossier Format ICH CTD / ACTD - Standard route requirement',
        'Product Dossier Content - Quality data, stability, specs, manufacturing process, safety/efficacy',
        'GMP certificate for manufacturing facility',
        'CoA and product specifications',
        'Packaging specimens, leaflets, labels',
        'Registration Committee Evaluation - Quality, safety, efficacy assessment',
        'Market Authorization issuance under BMRR',
        'Post-Approval Variations - CTD/ACTD documentation required for changes'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical / Food Supplement Pathway in Bhutan',
      steps: [
        'Classification determination - Food supplement vs medicine',
        'Local importer/firm setup as applicant',
        'Application Form VI-FPR under BMRR 2012',
        'Full composition preparation (qualitative + quantitative)',
        'COAs and microbiological reports',
        'Manufacturer GMP / ISO certification',
        'Labelling in English/local language - ingredients, storage, expiry, claims',
        'Compliance with Food Act 2005 and BFDA rules',
        'No unauthorized therapeutic claims verification',
        'Certificate of Free Sale for imported supplements (where required)',
        'BFDA evaluation and approval',
        'Registration issuance'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (CTD / ACTD)': [
        'Application Form I under BMRR (via Bhutanese importer)',
        'CTD / ACTD dossier - Administrative data, Quality/CMC, Non-clinical & clinical',
        'GMP certificate for manufacturing facility',
        'CoA and product specifications',
        'Packaging specimens, leaflets, labels',
        'Reliance documentation if using SRA approvals (emerging practice)',
        'API & FPP specifications, manufacturing, stability data'
      ],
      'Nutraceuticals / Food Supplements': [
        'Application Form VI-FPR under BMRR',
        'Full product formula (APIs, excipients, daily serving amounts)',
        'Finished product COA and microbiological reports',
        'Manufacturer GMP / ISO certification',
        'Label & artwork per Food Act and BFDA rules',
        'Ingredient list, lot/batch, expiry, storage info',
        'No unauthorized therapeutic claims',
        'Certificate of Free Sale (where required)'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Bhutan Should Be in Your "Cluster Strategy"',
      reasons: [
        'Import-dependent: ~USD 8.9M pharma imports, almost entirely imported',
        'Little local pharmaceutical manufacturing',
        'High NCD burden: Diabetes ~13-14% in adults, obesity rising',
        'Strong policy focus on health financing and UHC',
        'Government health spending ~3% of GDP (high for small economy)',
        'Unified authority (BFDA) simplifies oversight across medicines and food supplements',
        'Ideal to combine with India, Nepal, Bangladesh as Himalayan-South Asia regulatory cluster',
        'Shared CTD/ACTD dossiers across regional markets'
      ],
      bestPortfolio: [
        'Pharma: High-quality generics (antidiabetics, antihypertensives, lipid-lowering, anti-infectives, paediatrics)',
        'Nutra: Metabolic health, micronutrient supplements, cardio & antioxidant blends, maternal & child nutrition'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Bhutan',
      services: [
        'Portfolio & Cluster Strategy - Position Bhutan as part of Himalayan + NE India export cluster',
        'CTD / ACTD Dossier Preparation - Meet Guideline for Registration 2020 and BMRR requirements',
        'Food Supplement & Nutra Positioning - Classify correctly, prepare Form VI-FPR documentation',
        'Label & Claims Compliance - Adapt for Bhutan food and drug regulations',
        'Reliance & Lifecycle Support - Incorporate BFDA evolving practices, manage post-approval variations',
        'Regional Integration - Use shared CTD/ACTD assets across South Asia markets'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines and supplements in Bhutan?',
        answer: 'The Bhutan Food and Drug Authority (BFDA) regulates food, medicines, narcotics and agricultural products. It was formed by merging the Drug Regulatory Authority (DRA), BNCA and BAFRA.'
      },
      {
        question: 'What dossier format is required for medicine registration?',
        answer: 'BFDA\'s guidelines specify that ICH CTD or ASEAN CTD (ACTD) dossiers are required for the standard route of registration.'
      },
      {
        question: 'Is a local Bhutanese company needed to register products?',
        answer: 'Yes. Applications are made by a local applicant/importer using forms specified under the Bhutan Medicines Rules and Regulations. BFDA\'s G2C portal provides services like import authorization.'
      },
      {
        question: 'How are food supplements regulated?',
        answer: 'Food supplements are registered with BFDA (previously DRA) under BMRR 2012 (Form VI-FPR) and must comply with the Food Act 2005 and related food standards, including labelling and safety.'
      },
      {
        question: 'Is Bhutan a viable standalone market?',
        answer: 'On its own, Bhutan is small, but as part of a regional cluster with India, Nepal and Bangladesh, its CTD/ACTD-aligned, import-dependent system makes it strategically attractive for companies already active in South Asia.'
      }
    ]
  };

  return <CountryTemplate countryData={bhutanData} />;
};
